import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Divider
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'

interface SyncDialogProps {
  open: boolean
  onClose: () => void
  title: string
  isLoading: boolean
  error: string | null
  syncResult: any[] | null
  updatedCount: number
  createdCount?: number
  productLookupDataSync?: {
    collections: {
      created: number
      updated: number
      deactivated: number
      unchanged: number
    }
    colors: {
      created: number
      updated: number
      deactivated: number
      unchanged: number
      skipped?: number
    }
  } | null
  resultType: 'product' | 'account'
  syncWarning?: string | null
  missingSkus?: string[] | null
}

const formatValue = (val: any): string => {
  if (val === null || val === undefined) return 'null'
  if (typeof val === 'boolean') return val ? 'true' : 'false'
  if (val === '') return '""'
  if (typeof val === 'object') {
    return Object.entries(val)
      .map(([k, v]) => `${k}: ${Math.round((v as number) * 100)}%`)
      .join(', ')
  }
  return val.toString()
}

const SyncDialog: React.FC<SyncDialogProps> = ({
  open,
  onClose,
  title,
  isLoading,
  error,
  syncResult,
  updatedCount,
  createdCount = 0,
  productLookupDataSync,
  resultType,
  syncWarning,
  missingSkus
}) => {
  const canClose = !isLoading

  const createdItems: { identifier: string; changes: Record<string, any> }[] =
    []
  const flatRows: {
    identifier: string
    changeType?: string
    field: string
    from: string
    to: string
    isObject: boolean
  }[] = []

  if (syncResult) {
    syncResult.forEach(item => {
      const identifier =
        resultType === 'product' ? item.itemName : item.netsuiteCustomerId
      if (item.changeType === 'created') {
        createdItems.push({ identifier, changes: item.changes })
      } else {
        Object.entries(item.changes).forEach(([field, value]: any) => {
          flatRows.push({
            identifier,
            changeType: item.changeType,
            field,
            from: formatValue(value.from),
            to: formatValue(value.to),
            isObject:
              typeof value.from === 'object' || typeof value.to === 'object'
          })
        })
      }
    })
  }

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth='md'
      fullWidth
      disableScrollLock
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4
              }}
            >
              <CircularProgress size={60} />
              <Typography variant='body1' sx={{ mt: 2 }}>
                Syncing data, please wait...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity='error' icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {!isLoading && syncResult && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color='success' fontSize='large' />
                <Box>
                  <Typography variant='h6'>Sync Complete</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {updatedCount + createdCount}{' '}
                    {resultType === 'product' ? 'product' : 'account'}
                    {updatedCount + createdCount !== 1 ? 's' : ''} synced
                    successfully
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {resultType === 'product' && (
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  <Chip
                    label={`${createdCount} created`}
                    size='small'
                    color={createdCount > 0 ? 'success' : 'default'}
                  />
                  <Chip
                    label={`${updatedCount} updated`}
                    size='small'
                    color={updatedCount > 0 ? 'primary' : 'default'}
                  />
                  {productLookupDataSync && (
                    <>
                      <Chip
                        label={`PRODUCT_LOOKUP_DATA_SYNC collections: ${productLookupDataSync.collections.created} created, ${productLookupDataSync.collections.updated} updated, ${productLookupDataSync.collections.deactivated} inactive`}
                        size='small'
                        variant='outlined'
                      />
                      <Chip
                        label={`PRODUCT_LOOKUP_DATA_SYNC colors: ${
                          productLookupDataSync.colors.created
                        } created, ${
                          productLookupDataSync.colors.updated
                        } updated, ${
                          productLookupDataSync.colors.deactivated
                        } inactive, ${
                          productLookupDataSync.colors.skipped ?? 0
                        } skipped`}
                        size='small'
                        variant='outlined'
                      />
                    </>
                  )}
                </Stack>
              )}

              {syncWarning && (
                <Alert severity='warning' icon={<InfoIcon />}>
                  {syncWarning}
                  {missingSkus && missingSkus.length > 0 && (
                    <Typography
                      variant='caption'
                      component='pre'
                      sx={{ display: 'block', mt: 1, whiteSpace: 'pre-wrap' }}
                    >
                      {missingSkus.join(', ')}
                    </Typography>
                  )}
                </Alert>
              )}

              {syncResult.length > 0 ? (
                <Stack spacing={2}>
                  {createdItems.length > 0 && (
                    <Box>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        sx={{ mb: 1 }}
                      >
                        New ({createdItems.length})
                      </Typography>
                      <TableContainer>
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>
                                  {resultType === 'product'
                                    ? 'SKU'
                                    : 'NetSuite ID'}
                                </strong>
                              </TableCell>
                              {resultType === 'product' && (
                                <>
                                  <TableCell>
                                    <strong>Type</strong>
                                  </TableCell>
                                  <TableCell>
                                    <strong>Price</strong>
                                  </TableCell>
                                  <TableCell>
                                    <strong>Stock</strong>
                                  </TableCell>
                                  <TableCell>
                                    <strong>Weight</strong>
                                  </TableCell>
                                  <TableCell>
                                    <strong>Assembly</strong>
                                  </TableCell>
                                  <TableCell>
                                    <strong>Hinge</strong>
                                  </TableCell>
                                  <TableCell>
                                    <strong>Description</strong>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {createdItems.map((item, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                  >
                                    <Chip
                                      label='New'
                                      size='small'
                                      color='success'
                                      variant='outlined'
                                    />
                                    <Typography
                                      variant='body2'
                                      fontWeight='medium'
                                    >
                                      {item.identifier}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                {resultType === 'product' && (
                                  <>
                                    <TableCell>
                                      <Typography variant='body2'>
                                        {item.changes.itemType?.to ?? '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant='body2'>
                                        {item.changes.basePrice?.to != null
                                          ? `$${item.changes.basePrice.to}`
                                          : '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant='body2'>
                                        {item.changes.stock?.to ?? '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant='body2'>
                                        {item.changes.weight?.to ?? '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant='body2'>
                                        {item.changes.canBeAssembled?.to == null
                                          ? '—'
                                          : item.changes.canBeAssembled.to
                                          ? 'Y'
                                          : 'N'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant='body2'>
                                        {item.changes.canBeHinged?.to == null
                                          ? '—'
                                          : item.changes.canBeHinged.to
                                          ? 'Y'
                                          : 'N'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant='body2'
                                        sx={{
                                          maxWidth: 240,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title={
                                          item.changes.description?.to ?? ''
                                        }
                                      >
                                        {item.changes.description?.to ?? '—'}
                                      </Typography>
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {flatRows.length > 0 && (
                    <Box>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        sx={{ mb: 1 }}
                      >
                        Updated
                      </Typography>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size='small' stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>
                                  {resultType === 'product'
                                    ? 'SKU'
                                    : 'NetSuite ID'}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>Field</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Old Value</strong>
                              </TableCell>
                              <TableCell>
                                <strong>New Value</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {flatRows.map((row, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Typography
                                    variant='body2'
                                    fontWeight='medium'
                                  >
                                    {row.identifier}
                                  </Typography>
                                </TableCell>
                                <TableCell>{row.field}</TableCell>
                                <TableCell>
                                  {row.isObject ? (
                                    <Typography variant='body2'>
                                      {row.from}
                                    </Typography>
                                  ) : (
                                    <Chip
                                      label={row.from}
                                      size='small'
                                      color='default'
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {row.isObject ? (
                                    <Typography variant='body2'>
                                      {row.to}
                                    </Typography>
                                  ) : (
                                    <Chip
                                      label={row.to}
                                      size='small'
                                      color='success'
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {createdItems.length === 0 && flatRows.length === 0 && (
                    <Alert severity='info' icon={<InfoIcon />}>
                      No {resultType === 'product' ? 'products' : 'accounts'}{' '}
                      needed to be updated. All data is already in sync.
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Alert severity='info' icon={<InfoIcon />}>
                  No {resultType === 'product' ? 'products' : 'accounts'} needed
                  to be updated. All data is already in sync.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!canClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SyncDialog
